import React, { useState } from 'react';
import { useSearchParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { Lock, Eye, EyeOff, ArrowLeft, CheckCircle2 } from 'lucide-react';

export default function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get('token') || '';
  const isWelcome = params.get('welcome') === 'true';
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const tooShort = password.length > 0 && password.length < 8;
  const mismatch = confirm.length > 0 && confirm !== password;
  const canSubmit = token && password.length >= 8 && password === confirm && !loading;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { token, new_password: password });
      setDone(true);
      toast.success(isWelcome ? 'Account activated! Sign in to continue.' : 'Password updated. You can now sign in.');
      setTimeout(() => navigate('/login'), 1800);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to reset password.');
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-100 via-slate-50 to-[#E6F7F7]/30 flex items-center justify-center p-4">
        <Card className="w-full max-w-md shadow-xl">
          <CardHeader className="text-center pt-8">
            <CardTitle className="text-xl text-slate-800">{isWelcome ? 'Activation link missing' : 'Invalid reset link'}</CardTitle>
            <CardDescription>{isWelcome ? 'This activation link is missing a token.' : 'This password reset link is missing a token.'}</CardDescription>
          </CardHeader>
          <CardContent className="pb-8">
            <Link to="/forgot-password">
              <Button className="w-full" variant="outline">Request a new link</Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  const heading = done
    ? (isWelcome ? 'Account activated' : 'Password updated')
    : (isWelcome ? 'Welcome to Simplly' : 'Choose a new password');
  const subheading = done
    ? 'Redirecting you to the login page…'
    : (isWelcome ? 'Set a password to activate your account.' : 'Make it at least 8 characters.');
  const submitLabel = isWelcome ? (loading ? 'Activating…' : 'Activate account') : (loading ? 'Updating…' : 'Update password');

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-100 via-slate-50 to-[#E6F7F7]/30 flex items-center justify-center p-4">
      <Card className="w-full max-w-md shadow-xl border-slate-200/50 bg-white/80 backdrop-blur-sm relative overflow-hidden">
        <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-[#00A3A1] to-[#E94E89]" />

        <CardHeader className="space-y-3 text-center pt-8 pb-6">
          <div className="mx-auto">
            <img src="/simplly_logo.png" alt="Simplly Logo" className="h-14 mx-auto object-contain" />
          </div>
          <div>
            <CardTitle className="text-2xl font-bold text-slate-800">
              {heading}
            </CardTitle>
            <CardDescription className="text-slate-500 mt-1">
              {subheading}
            </CardDescription>
          </div>
        </CardHeader>

        <CardContent className="px-8 pb-8">
          {done ? (
            <div className="flex items-start gap-3 p-4 rounded-lg bg-emerald-50 border border-emerald-200">
              <CheckCircle2 className="h-5 w-5 text-emerald-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-emerald-900">You're all set. Redirecting…</p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="space-y-2">
                <Label htmlFor="new_password" className="text-sm font-medium text-slate-700">New password</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    id="new_password"
                    data-testid="reset-password-new-input"
                    type={showPassword ? 'text' : 'password'}
                    autoFocus
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="pl-10 pr-10 h-11 border-slate-200 focus:border-[#00A3A1] focus:ring-[#00A3A1]/20"
                    required
                    minLength={8}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700"
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
                {tooShort && <p className="text-xs text-amber-600">At least 8 characters required.</p>}
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirm_password" className="text-sm font-medium text-slate-700">Confirm password</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    id="confirm_password"
                    data-testid="reset-password-confirm-input"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="••••••••"
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    className="pl-10 h-11 border-slate-200 focus:border-[#00A3A1] focus:ring-[#00A3A1]/20"
                    required
                  />
                </div>
                {mismatch && <p className="text-xs text-red-600">Passwords don't match.</p>}
              </div>

              <Button
                type="submit"
                data-testid="reset-password-submit-button"
                disabled={!canSubmit}
                className="w-full h-11 bg-gradient-to-r from-[#00A3A1] to-[#00908E] text-white font-medium shadow-lg"
              >
                {submitLabel}
              </Button>

              <div className="pt-4 text-center text-sm">
                <Link to="/login" className="text-slate-600 hover:text-[#00A3A1] inline-flex items-center gap-1">
                  <ArrowLeft className="h-3.5 w-3.5" /> Back to login
                </Link>
              </div>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
